import { useAppDispatch, useAppSelector } from "../hooks";
import {
    connectSocket,
    disconnectSocket,
    sendMessage,
    clearMessageHistory,
} from "../../features/chatbot/socketMiddleware";

import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Divider, TextField, IconButton, Button, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import type { Message } from "../../features/chatbot/messagesSlice";

const Solution: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const enteredText: string = location.state ? location.state.enteredText : "";
    window.history.replaceState({}, ""); // reset state
    const [currentQuestion, setCurrentQuestion] = useState(enteredText);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const { messages, isLoading } = useAppSelector((state) => state.messages);

    // Effect hook to setup and cleanup the WebSocket connection
    useEffect(() => {
        // Setup WebSocket on component mount
        dispatch(connectSocket());
        return () => {
            // Cleanup WebSocket on component unmount
            dispatch(disconnectSocket());
        };
    }, []);

    // Effect hook to auto-scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    function handleSendQuestion(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (currentQuestion.trim() === "" || isLoading) return;

        // send message through websocket
        dispatch(sendMessage(currentQuestion, "You"));
        setCurrentQuestion("");
    }

    function handleAskInForum() {
        // navigate to the AskQuestion page with the first question
        if (messages.length > 0) {
            navigate("/question/new", { state: { enteredText: messages[0].message } });
        }
    }

    function handleResetChat() {
        // reset the chat history
        dispatch(clearMessageHistory());
    }

    // function to render each message
    function renderMessage(message: Message, index: number) {
        if (message.user === "You") {
            return (
                <Box key={index}>
                    <Typography
                        variant="h6"
                        sx={{ fontFamily: "Roboto, sans-serif", color: "#00796b", fontWeight: "bold" }}
                    >
                        {index == 0 ? "You asked:" : "Add-on question " + index / 2}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ fontFamily: "Roboto, sans-serif", color: "#222", marginBottom: 1 }}
                    >
                        {message.message}
                    </Typography>
                    <Divider sx={{ marginBottom: 1 }} />
                </Box>
            );
        } else {
            return (
                <Box key={index}>
                    <Typography
                        variant="h6"
                        sx={{ fontFamily: "Roboto, sans-serif", color: "#00796b", fontWeight: "bold" }}
                    >
                        Answer:
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "Roboto, sans-serif", color: "#666" }}>
                        {message.message}
                    </Typography>
                    {index < messages.length - 1 && <Divider sx={{ marginY: 5 }} />}
                </Box>
            );
        }
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            padding={1}
            sx={{ bgcolor: "#83AF9B" }}
        >
            <IconButton onClick={() => navigate(-1)} sx={{ position: "absolute", top: 20, left: 20 }}>
                <ArrowBackIcon />
            </IconButton>
            <Paper
                elevation={10}
                sx={{
                    padding: 4,
                    maxWidth: "900px",
                    width: "100%",
                    borderRadius: 4,
                    marginBottom: 4,
                    overflowY: "auto",
                    maxHeight: "75vh",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                    background: "#ffffff",
                }}
            >
                <Typography
                    variant="h5"
                    marginBottom={2}
                    align="center"
                    sx={{
                        textShadow: `
                        1px 1px 1px rgba(0, 0, 0, 0.1),
                        2px 2px 2px rgba(0, 0, 0, 0.2),
                        3px 3px 3px rgba(0, 0, 0, 0.1)`,
                        color: "green",
                        fontStyle: "italic",
                        fontFamily: "Georgia",
                    }}
                >
                    Solutions generated from Natural Intelligence
                </Typography>
                <Divider sx={{ marginBottom: 2 }} />
                {messages.map((msg, index) => renderMessage(msg, index))}
                <div ref={messagesEndRef} /> {/* Invisible element to help scroll into view */}
                <Stack direction="row" spacing={2} paddingTop={2}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleAskInForum}
                        disabled={messages.length === 0}
                        sx={{ marginTop: 2, alignSelf: "center" }}
                    >
                        Ask in Forum
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleResetChat}
                        disabled={messages.length === 0}
                        sx={{ marginTop: 2, alignSelf: "center" }}
                    >
                        Reset Chat
                    </Button>
                </Stack>
            </Paper>
            <Paper
                elevation={10}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: 2,
                    maxWidth: "800px",
                    width: "100%",
                    borderRadius: 4,
                    position: "fixed",
                    bottom: 20,
                    bgcolor: "#ffffff",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={handleSendQuestion}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Ask another question..."
                    value={currentQuestion}
                    onChange={(e) => setCurrentQuestion(e.target.value)}
                    sx={{ marginRight: 2 }}
                />
                <IconButton color="primary" type="submit" disabled={currentQuestion.trim() === "" || isLoading}>
                    <SendIcon />
                </IconButton>
            </Paper>
        </Box>
    );
};

export default Solution;
